// import { useQuery } from "@tanstack/react-query";
// import { CheckCircle, Bookmark } from "lucide-react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { useState, useEffect, useRef, useCallback } from "react";
import { CATALOG_CODES } from "../../constants/catalog";
import { CurrencyText } from "../common/CurrencyText";
import { EventPackageType } from "../../constants/event-package";
import { formatDateV2 } from "../../helpers/dateTimeHelper";
import { DATETIME_FORMATS } from "../../constants/datetime-formats";
import { getCurrentUserFromLocalStorage, getI18nLanguageFromLocalStorage } from "../../helpers/localStorageHelper";
import { postEventPackageToShoppingCart } from "../../services/eventPackageService";
import { ErrorTemplate } from "../common/ToastMessage/Template";
import EventPackageLinkedItem from "../EventPackageLinkedItem";
import { CURRENCY } from "../../constants/currency";
import LoadingButton from "../common/LoadingButton";
import { translate } from "../../localization/translateService";

// Custom hook declared at module scope to avoid TDZ and follow hooks rules
const useTranslations = (lang) => {
  const [translations, setTranslations] = useState({
    PRICE_SAVE: "",
  });

  const loadTranslations = useCallback(async () => {
    const [PRICE_SAVE] = await Promise.all([
      translate("common_ns:PRICE_SAVE"),
    ]);

    setTranslations({
      PRICE_SAVE,
    });
  }, []);

  useEffect(() => {
    loadTranslations();
  }, [lang, loadTranslations]);

  return { translations };
};

export default function EventDetailFeaturedCourse({ data, type, trans }) {
  // const { data: course, isLoading } = useQuery({
  //   queryKey: ["/api/featured-course"],
  // });

  const toast = useRef(null);

  const [course, setCourse] = useState(data);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const isEvent = type === EventPackageType.EVENT;

  const currentUser = getCurrentUserFromLocalStorage();
  const language = getI18nLanguageFromLocalStorage();

  const { translations } = useTranslations(language);

  const defaultQuantity = 1;
  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.getElementById('hero-section');
      if (heroSection) {
        const heroBottom = heroSection.offsetTop + heroSection.offsetHeight;
        const scrollPosition = window.scrollY;
        setIsCollapsed(scrollPosition > heroBottom - 100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  

  // Calculate days remaining until event - fixed to show D-25
  const getDaysRemaining = (eventDateString) => {
    return 25; // Fixed value as requested
  };

  if (isLoading) {
    return (
      <section style={{ backgroundColor: 'white', padding: '3rem 0' }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-content-center">
            <ProgressSpinner />
          </div>
        </div>
      </section>
    );
  }

  if (!course) {
    return (
      <section style={{ backgroundColor: 'white', padding: '3rem 0' }}>
        <div className="container">
          <div className="text-center">
            <p style={{ color: '#6b7280' }}>No featured course available</p>
          </div>
        </div>
      </section>
    );
  }

  const uniqueItemLabels = () => {
    let labels = [];

    if (course.selectedVODs.length > 0) {
      labels.push(trans.vodLabel);
    }

    if (course.selectedSeries.length > 0) {
      labels.push(trans.seriesLabel);
    }

    if (course.selectedTrainers.length > 0) {
      labels.push(trans.tutorTicketLabel);
    }

    if (course.selectedCoupons.length > 0) {
      labels.push(trans.availableCouponsLabel);
    }

    return labels.join(', ');
  }


  const priceLabel = (data.price != null && data.currency) ? (
    <CurrencyText amount={data.price} sourceCurrency={data.currency.currencyCode} />
  ) : '';

  const originalPriceLabel = (data.originalPrice != null) ? (
    <CurrencyText amount={data.originalPrice} sourceCurrency={data?.currencyCode} />
  ) : "";

  const savePriceLabel = (data.originalPrice != null) ? (
    <CurrencyText amount={data.originalPrice - data.price} sourceCurrency={data?.currencyCode} />
  ) : "";


  const remainLabel =
    isEvent && data.daysRemaining != null ? `D-${data.daysRemaining}` : '';

  const prettyEnd =
    isEvent && data.endDate
      ? formatDateV2(data.endDate, DATETIME_FORMATS.DoMMMMMYYYY)
      : '';

  const isExpired = isEvent && data.endDate
    ? new Date(data.endDate) < new Date()
    : false;
  const isPurchased = !!course?.isPurchased;

  const handleEventBuyNow = () => {
    setIsAddingToCart(true);
    if (!currentUser) {
      window.location.href = `/auth/login?lang=${language}&returnUrl=${window.location.pathname}`;
      return;
    }

    const productPublicId = data?.product?.publicId;
    if (!productPublicId) return;
    const query = new URLSearchParams({
      isQuickBuy: true,
      productId: productPublicId,
      quantity: 1
    }).toString();
    window.location.href = `/${language}/shopping-cart?${query}`;
  };

  const handleAddToCart = () => {
    setIsAddingToCart(true);
    if (!currentUser) {
      window.location.href = `/auth/login?lang=${language}&returnUrl=${window.location.pathname}`;
      return;
    }

    const productPublicId = data?.product?.publicId;
    if (!productPublicId) return;

    postEventPackageToShoppingCart(productPublicId)
      .then(() => {
        const query = new URLSearchParams({
          isQuickBuy: true,
          productId: productPublicId,
          quantity: 1
        }).toString();
        window.location.href = `/${language}/shopping-cart?${query}`;
      })
      .catch((error) => {
        setIsAddingToCart(false);
        toast.current?.show({
          severity: 'error',
          summary: trans?.errorTitle,
          detail: trans?.errorAddingToCart,
          life: 3000,
          position: 'bottom-right',
          content: (props) => ErrorTemplate(props)
        });
      });
  };

  return (
    <>
      {/* Main Hero Section - Course Overview */}
      <section id="hero-section">
        <div className="container">
          <div className="grid">
            <div className="col-12 lg:col-5">
              <img
                src={course.thumbnailUrl}
                alt="Korean language learning workshop"
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  width: '100%',
                  // height: '400px',
                  objectFit: 'cover',
                  aspectRatio: '16/9'
                }}
              />
            </div>

            <div className="col-12 lg:col-7 flex flex-column">
              {/* Event Badge */}
              <div className="event-package__meta">
                <i className="pi pi-bookmark-fill event-package__bookmark-icon" />
                <span className="event-package__date-text keduall-typography heading-h6-normal-400">
                  {isEvent ? trans.eventLabel : trans.packageLabel}
                </span>

                {isEvent && remainLabel && (
                  <Tag
                    value={remainLabel}
                    className="event-package__remain-inline keduall-typography text-14-semibold-600 p-tag p-component"
                  />
                )}

                {isEvent && prettyEnd && (
                  <span className="event-package__date-text keduall-typography heading-h6-normal-400">
                    {prettyEnd}
                  </span>
                )}
              </div>

              {/* Course Title */}
              <h3 className="keduall-typography heading-h3-semibold-600 mt-3 mb-3">
                {course.title}
              </h3>

              {course.shortDescription && (
                <p className="keduall-typography text-16-normal-400 flex-grow-1 mb-3">
                  {course.shortDescription}
                </p>
              )}

              {/* Pricing and CTA */}
              <Card className="event-detail-featured-course-card">
                <div className="flex align-items-center justify-content-between">
                  <div>
                    <p class="keduall-typography text-14-normal-400 mb-1">
                      {uniqueItemLabels()}
                    </p>
                    <div className="flex flex-column gap-2 align-items-start mt-2">
                      {data.discountPercent > 0 && (
                        <h6 className="keduall-typography heading-h6-normal-400 line-through">
                          {data.originalPrice >= data.price ? originalPriceLabel : "-"}
                        </h6>
                      )}
                      <div className="flex align-items-center justify-content-start w-full gap-2">
                        <h3 className="keduall-typography heading-h3-semibold-600 text-danger-txt">
                          {priceLabel}
                        </h3>
                        {data.discountPercent > 0 && (
                          <span style={{ backgroundImage: `url(${trans?.promotionBadgeImage})`}}
                          className="keduall-typography text-12-normal-400 text-white -mt-5 event-detail-featured-course-badge"
                        >
                          -{data.discountPercent}%
                        </span>
                        )}
                      </div>
                      {data.discountPercent > 0 &&
                        <div className="event-detail-featured-course-price-save">{translations.PRICE_SAVE} {savePriceLabel}</div>
                      }
                    </div>

                  </div>
                  {/* The add to cart button*/}
                  {!isExpired && !isPurchased && (
                      <LoadingButton
                          icon="pi pi-arrow-up-right"
                          iconPos="right"
                          className="keduall-buttons button-filled-default-large-primary keduall-typography heading-h6-semibold-600"
                          onClick={(e) => {
                              e.stopPropagation();
                              handleEventBuyNow();
                          }}
                          label={trans.buyNowLabel}
                          loading={isAddingToCart}
                      />
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Course Content Section */}
      <section>
        <div className="container">
          {/* Course Features */}
          <Card style={{
            backgroundColor: 'white',
            padding: '2rem',
            marginBottom: '1.5rem',
            border: 'none',
            boxShadow: '0 0 5px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <div
              className="event-package__content keduall-typography text-16-normal-400"
              dangerouslySetInnerHTML={{ __html: course.content }}
            />
            {course.buttonTitle && course.buttonUrl && (
              <div className="flex justify-content-center mt-5">
                <Button
                  label={course.buttonTitle}
                  className="keduall-buttons button-filled-default-large-primary keduall-typography heading-h6-semibold-600"
                  onClick={() =>
                    window.open(course.buttonUrl, '_blank', 'noopener')
                  }
                />
              </div>
            )}

          </Card>
        </div>
      </section>

      {/* VOD */}
      {course.selectedVODs.length > 0 &&
        <section>
          <div className="container ">
            <Card title={trans.vodLabel} style={{
              backgroundColor: 'white',
              padding: '2rem',
              marginBottom: '1.5rem',
              border: 'none',
              boxShadow: '0 0 5px 0 rgba(0, 0, 0, 0.1)'
            }}>
              {/* Event Package Content  */}
              <div className="event-package__additional-info mt-3">
                <EventPackageLinkedItem
                  // title={trans.vodLabel}
                  items={data.selectedVODs.map(v => ({
                    label: v.title,
                    value: <CurrencyText amount={v.originalPrice * (1 - ((course.discountPercent ?? 0) / 100))} sourceCurrency={v.currencyCode ?? CURRENCY.USD} />,
                    colorClass: 'text-help-txt'
                  }))}
                />
              </div>
            </Card>
          </div>
        </section>
      }

      {/* Series */}
      {course.selectedSeries.length > 0 &&
        <section>
          <div className="container">
            <Card title={trans.seriesLabel} style={{
              backgroundColor: 'white',
              padding: '2rem',
              marginBottom: '1.5rem',
              border: 'none',
              boxShadow: '0 0 5px 0 rgba(0, 0, 0, 0.1)'
            }}>
              {/* Event Package Content  */}
              <div className="event-package__additional-info mt-3">
                <EventPackageLinkedItem
                  // title={trans.seriesLabel}
                  items={data.selectedSeries.map(s => ({
                    label: s.title,
                    value: <CurrencyText amount={s.originalPrice * (1 - ((course.discountPercent ?? 0) / 100))} sourceCurrency={s.currencyCode ?? CURRENCY.USD} />,
                    colorClass: 'text-help-txt'
                  }))}
                />
              </div>
            </Card>
          </div>
        </section>
      }

      {/* Tutor Ticket */}
      {course.selectedTrainers.length > 0 &&
        <section>
          <div className="container">
            <Card title={trans.tutorTicketLabel} style={{
              backgroundColor: 'white',
              padding: '2rem',
              marginBottom: '1.5rem',
              border: 'none',
              boxShadow: '0 0 5px 0 rgba(0, 0, 0, 0.1)'
            }}>
              {/* Event Package Content  */}
              <div className="event-package__additional-info mt-3">
                <EventPackageLinkedItem
                  // title={trans.tutorTicketLabel}
                  items={course.selectedTrainers.map(t => ({
                    label: t.fullName,
                    value: <CurrencyText amount={(t.pricePerLesson * (t.bundleDiscount.numberTicket ?? defaultQuantity)) * (1 - ((course.discountPercent ?? 0) / 100))} sourceCurrency={t.currencyCode ?? CURRENCY.USD} />,
                    colorClass: 'text-help-txt',
                    labelStyleClass: 'cursor-pointer hover:underline',
                    onLabelClick: () => {
                      if (t.trainerPublicId) {
                        window.location.href = `/${language}/real-time-lectures/${t.trainerPublicId}`;
                      }
                    }
                  }))}
                />
              </div>
            </Card>
          </div>
        </section>
      }

      {/* Available Coupons */}
      {course.selectedCoupons.length > 0 &&
        <section>
          <div className="container">
            <Card title={trans.availableCouponsLabel} style={{
              backgroundColor: 'white',
              padding: '2rem',
              marginBottom: '1.5rem',
              border: 'none',
              boxShadow: '0 0 5px 0 rgba(0, 0, 0, 0.1)'
            }}>
              {/* Event Package Content  */}
              <div className="event-package__additional-info mt-3">
                <EventPackageLinkedItem
                  // title={trans.availableCouponsLabel}
                  items={course.selectedCoupons.map(c => ({
                    label: c.name,
                    value: c.isExpired ? trans.expiredLabel : trans.activeLabel,
                    colorClass: c.isExpired ? 'text-danger-txt' : 'text-success-txt',
                  }))}
                />
              </div>
            </Card>
          </div>
        </section>
      }

      {/* Sticky Collapsed Header */}
      <div
        style={{
          position: 'fixed',
          top: "8.6rem",
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e7eb',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          transform: isCollapsed ? 'translateY(0)' : 'translateY(-100%)',
          opacity: isCollapsed ? 1 : 0
        }}
      >
        <div className="container mx-auto px-4">
          {/* Desktop Layout */}
          <div className="hidden md:flex align-items-center justify-content-between py-3">
            <div className="flex align-items-center gap-4">
              {/* Event Badge */}
              <div className="event-package__meta">
                <i className="pi pi-bookmark-fill event-package__bookmark-icon" />
                <span className="event-package__date-text keduall-typography heading-h6-normal-400">
                  {isEvent ? trans.eventLabel : trans.packageLabel}
                </span>

                {isEvent && remainLabel && (
                  <Tag
                    value={remainLabel}
                    className="event-package__remain-inline keduall-typography text-14-semibold-600 p-tag p-component"
                  />
                )}

                {/* {isEvent && prettyEnd && (
                  <span className="event-package__date-text keduall-typography heading-h6-normal-400">
                    {prettyEnd}
                  </span>
                )} */}
              </div>
              <h5 className="keduall-typography heading-h5-semibold-600 one-line-text">
                {course.title}
              </h5>
            </div>
            <div className="flex align-items-center gap-4">
              <h5 className="keduall-typography heading-h5-semibold-600 text-danger-txt">
                {priceLabel}
              </h5>
              {!isExpired && !isPurchased && <LoadingButton
                label={trans.buyNowLabel}
                style={{
                  backgroundColor: 'var(--orange-color)',
                  border: 'none',
                  padding: '0.5rem 1.5rem',
                  fontWeight: '500',
                  borderRadius: '2rem'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEventBuyNow();
                }}
                loading={isAddingToCart}
              />}
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden py-3">
            <div className="flex align-items-center justify-content-between">
              <div className="flex align-items-center gap-2 flex-1" style={{ minWidth: 0 }}>
                {/* Event Badge */}
                <div className="event-package__meta">
                  {/* <i className="pi pi-bookmark-fill event-package__bookmark-icon" />
                <span className="event-package__date-text keduall-typography heading-h6-normal-400">
                  {isEvent ? trans.eventLabel : trans.packageLabel}
                </span> */}

                  {/* {isEvent && remainLabel && (
                  <Tag
                    value={remainLabel}
                    className="event-package__remain-inline keduall-typography text-14-semibold-600 p-tag p-component"
                  />
                )} */}

                  {/* {isEvent && prettyEnd && (
                  <span className="event-package__date-text keduall-typography heading-h6-normal-400">
                    {prettyEnd}
                  </span>
                )} */}
                </div>
                <h5 className="keduall-typography heading-h5-semibold-600 one-line-text mb-2">
                  {course.title}
                </h5>
              </div>
              <h5 className="keduall-typography heading-h5-semibold-600 text-danger-txt">
                {priceLabel}
              </h5>
            </div>
            <Button
              label={trans.buyNowLabel}
              className="w-full"
              style={{
                backgroundColor: 'var(--orange-color)',
                border: 'none',
                padding: '0.5rem',
                fontWeight: '500',
                borderRadius: '2rem'

              }}
              onClick={(e) => {
                e.stopPropagation();
                handleEventBuyNow();
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
